/**
 * `BriefOwnerSelect` — editable dropdown for the brief owner.
 *
 * Reads from the hardcoded `mockWorkspaceUsers` fixture and owns its own
 * controlled value via `useState`. When the BE work  lands,
 * the prop surface stays the same — only the data source and the mutation
 * dispatch swap in.
 *
 * Visual contract per Figma:
 *   - Trigger height matches an Input field (default size). Selected value
 *     shows avatar + name (matching the Figma "G" avatar + name pattern).
 *   - Dropdown rows show avatar + name. The currently-selected row gets
 *     Radix's built-in check, which we keep.
 *
 * No effects — `Select`'s value is plain controlled state, and `onValueChange`
 * is an event callback.
 */
import { useCallback, useState } from 'react';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';

import { getInitials } from '@/shared/utils/getInitials';

import type { UserSummary } from './types';
import { useTranslation } from 'react-i18next';

export interface BriefOwnerSelectProps {
  users: UserSummary[];
  initialOwnerId: string;
  /**
   * Optional DOM `id` placed on the rendered SelectTrigger button so a
   * sibling `<label htmlFor={...}>` resolves to it and clicking the label
   * focuses / opens the select. Without this, Radix auto-generates an id
   * the caller can't reference. `aria-label` still carries the accessible
   * name as a fallback when no visible label is wired.
   */
  triggerId?: string;
  /**
   * Optional event hook fired when the user picks a different owner.
   * When BE lands this will dispatch the owner-change mutation;
   * for now it's a no-op so the component stays self-contained.
   */
  onOwnerChange?: (ownerId: string) => void;
}

export function BriefOwnerSelect({
  users,
  initialOwnerId,
  triggerId,
  onOwnerChange,
}: BriefOwnerSelectProps) {
  const { t } = useTranslation('projects');

  // Controlled value lives here so the trigger keeps showing the picked user
  // even though nothing is persisted. Replace with `value={brief.ownerId}`
  // + `onValueChange={updateOwner}` once the BE is wired.
  const [ownerId, setOwnerId] = useState(initialOwnerId);

  const handleChange = useCallback(
    (next: string) => {
      setOwnerId(next);
      onOwnerChange?.(next);
    },
    [onOwnerChange],
  );

  const selected = users.find((u) => u.id === ownerId);

  return (
    <Select value={ownerId} onValueChange={handleChange}>
      <SelectTrigger
        id={triggerId}
        className="w-full"
        data-testid="brief-owner-select-trigger"
        aria-label={t('projectBriefDetail.briefOwnerSelect.briefOwner')}
      >
        <SelectValue>{selected ? <OwnerOptionLabel user={selected} /> : null}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {users.map((user) => (
          <SelectItem key={user.id} value={user.id}>
            <OwnerOptionLabel user={user} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Compact avatar + name row reused by both the trigger value and each
 * dropdown option, so picked state and option state look identical.
 */
function OwnerOptionLabel({ user }: { user: UserSummary }) {
  return (
    <span className="flex items-center gap-2">
      <Avatar size="xs" roundness="full">
        {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
        <AvatarFallback size="xs">{getInitials(user.name)}</AvatarFallback>
      </Avatar>
      <span className="text-sm text-foreground">{user.name}</span>
    </span>
  );
}
